import { Button, IconProps } from '@platform/components';
import { useEffect, useState } from 'react';
import {
  ProfessionalProfileResponseIntroCtaState,
  ProjectAvailableForIntro,
  SentIntroDetails,
} from '@platform/client/schemas';
import IntroductionSentModal from './IntroductionSentModal';
import SendIntroModal from './SendIntroModal';
import ViewIntroductionModal from './ViewIntroductionModal';

export type CTAState = {
  icon: IconProps['type'];
  buttonText: string;
  isHidden: boolean;
  onClick: () => void;
};

// TODO: Update the following explanation for the newest revision of the professionals page for August 2025, after
// some behavior questions are clarified.
/** Determines which version of the CTA to show, determining whether the current user can send a new
 * intro or view an existing conversation. Also determines the "description" text just beneath the CTA.
 *
 * As of 2025-07-29, our logic here has been drastically simplified. We do not observe the user's seeking
 * status, project assignment, current introductions, unless those intros were sent within 30 days.
 *
 * Thus, we allow sending a message if no intro has been sent within 30 days. Otherwise, an intro does exist
 * and the CTA allows viewing it on the My Projects page. */
const getCtaState = (
  introCtaState: ProfessionalProfileResponseIntroCtaState | undefined,
  showExistingIntroModal: (introId: number) => void,
  showNewIntroModal: () => void,
  showIntro: () => void,
): CTAState | null => {
  if (!introCtaState) {
    return null; // No user data, no CTA
  }

  switch (introCtaState.state) {
    case 'hidden':
      return {
        icon: 'edit-04',
        buttonText: '',
        isHidden: true,
        onClick: () => {},
      };
    // Currently, receiving or sending an intro does not change the UX to allow you to view the intro
    // or match the user. We also don't even direct to the My Projects page
    case 'one_project_intro_received':
    case 'one_project_intro_sent':
      return {
        icon: 'message-text-square-01',
        buttonText: 'View introduction',
        isHidden: false,
        onClick: () => showIntro(),
      };
    case 'can_send_intro_received':
    case 'can_send_intro_sent':
      return {
        icon: 'edit-04',
        buttonText: 'Send introduction',
        isHidden: false,
        onClick: () => showNewIntroModal(),
      };
    case 'can_send':
      return {
        icon: 'edit-04',
        buttonText: 'Send introduction',
        isHidden: false,
        onClick: () => showNewIntroModal(),
      };
    case 'matched':
      return {
        icon: 'message-text-square-01',
        buttonText: 'View introduction',
        isHidden: false,
        onClick: () => showIntro(),
      };
  }
};

/** A button for sending or viewing an introduction on a directory page. Encapsulates state around
 * the CTA button for sending intros & several suppporting modals.
 */
const IntroductionCTA = ({
  apiCtaState,
  recipientName,
  otherPartyType,
  buttonProps,
  presetProjectId,
  requireProjectSelection,
}: {
  apiCtaState: ProfessionalProfileResponseIntroCtaState;
  recipientName: string;
  otherPartyType: 'professional' | 'organization';
  buttonProps?: Partial<React.ComponentProps<typeof Button>>;
  presetProjectId?: number;
  requireProjectSelection?: boolean;
  canViewIntroBeforeMatch?: boolean;
}) => {
  const [showRequestIntroModal, setShowRequestIntroModal] = useState(false);
  const [showRequestSentModal, setShowRequestSentModal] = useState(false);
  const [showSentIntroModal, setShowSentIntroModal] = useState(false);
  const [projects, setProjects] = useState<ProjectAvailableForIntro[]>([]);

  useEffect(() => {
    if (
      apiCtaState?.state === 'can_send' ||
      apiCtaState?.state === 'can_send_intro_received' ||
      apiCtaState?.state === 'can_send_intro_sent'
    ) {
      setProjects(apiCtaState.uncontactedProjects ?? []);
    }
  }, [apiCtaState?.state, apiCtaState]);

  const showIntro = () => {
    if (apiCtaState?.state === 'hidden' || apiCtaState?.state === 'can_send') {
      console.error('No introduction to view');
      return;
    }

    setShowSentIntroModal(true);
  };

  const ctaState = getCtaState(
    apiCtaState,
    showIntro,
    () => setShowRequestIntroModal(true),
    () => showIntro(),
  );

  if (!apiCtaState || !ctaState) {
    return null;
  }

  return (
    <>
      <div className='w-full sm:w-auto' data-cta-state={apiCtaState.state}>
        {!ctaState.isHidden && (
          <Button
            size='lg'
            variant='primary'
            leadIcon={ctaState.icon}
            additionalClassnames='w-full'
            onClick={ctaState.onClick}
            {...buttonProps}
          >
            {ctaState.buttonText}
          </Button>
        )}
      </div>
      {showRequestIntroModal && (
        <SendIntroModal
          isOpen={showRequestIntroModal}
          setIsOpen={setShowRequestIntroModal}
          presetProjectId={presetProjectId}
          projectsAvailableForIntro={projects}
          onMessageSent={() => {
            setShowRequestIntroModal(false);
            setShowRequestSentModal(true);
          }}
          requireProjectSelection={requireProjectSelection ?? false}
        />
      )}
      {showRequestSentModal && (
        <IntroductionSentModal
          recipientName={recipientName}
          isOpen={showRequestSentModal}
          setIsOpen={setShowRequestSentModal}
        />
      )}
      {showSentIntroModal &&
        (apiCtaState.state === 'matched' ||
          apiCtaState.state === 'can_send_intro_received' ||
          apiCtaState.state === 'can_send_intro_sent' ||
          apiCtaState.state === 'one_project_intro_received' ||
          apiCtaState.state === 'one_project_intro_sent') && (
          <ViewIntroductionModal
            isOpen={showSentIntroModal}
            setIsOpen={setShowSentIntroModal}
            otherPartyType={otherPartyType}
            details={apiCtaState.details as SentIntroDetails}
          />
        )}
    </>
  );
};

export default IntroductionCTA;
